import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banknote, Plus, Pencil, Trash2, Search, AlertCircle, Wallet } from 'lucide-react'
import { apiJson, apiFetch, type Page } from '../api'
import { hasAnyRole } from '../auth'
import Modal from '../components/Modal'
import Pager from '../components/Pager'
import SortableTh from '../components/SortableTh'

interface Payslip {
  id: string
  employeeId: string
  employeeName: string
  employeeCode: string
  period: string
  baseSalary: number
  allowances: number
  deductions: number
  netPay: number
  currency: string // from the organization profile (default MMK)
  status: 'draft' | 'issued' | 'paid'
  notes: string | null
  createdAt: string
}

interface Employee {
  id: string
  employeeCode: string
  firstName: string
  lastName: string
}

interface PayrollRun {
  id: string
  period: string
  status: 'draft' | 'finalized'
  payslipsCreated: number
  totalEmployees: number
  totalNet: number
  orgUnitId?: string | null
  generatedBy?: string
  finalizedBy?: string
  finalizedAt?: string
  notes?: string
  createdAt: string
}

interface DeptSummary {
  orgUnitId: string | null
  orgUnitName: string
  employees: number
  totalBase: number
  totalAllowances: number
  totalDeductions: number
  totalNet: number
}

const statusMeta: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-500 ring-slate-200' },
  issued: { label: 'Issued', cls: 'bg-blue-50 text-blue-700 ring-blue-100' },
  paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
}

function money(n: number, currency: string) {
  return `${n.toLocaleString('en-US')} ${currency}`
}

function periodLabel(p: string) {
  const [y, m] = p.split('-').map(Number)
  return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

export default function Payroll() {
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')
  const allowDelete = hasAnyRole('admin')
  const [tab, setTab] = useState<'mine' | 'all' | 'runs'>('mine')

  // my payslips
  const [myPage, setMyPage] = useState<Page<Payslip> | null>(null)
  const [myPageNum, setMyPageNum] = useState(1)
  const [mySize, setMySize] = useState(10)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [currency, setCurrency] = useState('MMK')

  // organization currency (defaults to MMK when unprovisioned)
  useEffect(() => {
    apiJson<{ currency?: string } | null>('/organization')
      .then((o) => setCurrency(o?.currency ?? 'MMK'))
      .catch(() => {})
  }, [])

  // all payslips (admin/hr)
  const [allPage, setAllPage] = useState<Page<Payslip> | null>(null)
  const [allPageNum, setAllPageNum] = useState(1)
  const [allSize, setAllSize] = useState(20)
  const [allSort, setAllSort] = useState('period')
  const [allOrder, setAllOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [query, setQuery] = useState('')
  const [q, setQ] = useState('')
  const [periodFilter, setPeriodFilter] = useState('')
  const [empFilter, setEmpFilter] = useState('')
  const [deptFilter, setDeptFilter] = useState('')
  const [orgUnits, setOrgUnits] = useState<{ id: string; name: string }[]>([])

  // manage
  const [employees, setEmployees] = useState<Employee[]>([])
  const [actionError, setActionError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // payroll runs
  const [runs, setRuns] = useState<PayrollRun[]>([])
  const [runDetail, setRunDetail] = useState<PayrollRun | null>(null)
  const [runSummary, setRunSummary] = useState<DeptSummary[]>([])
  const [runBusy, setRunBusy] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(query.trim())
      setAllPageNum(1)
    }, 300)
    return () => clearTimeout(t)
  }, [query])

  const fetchMine = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(myPageNum), size: String(mySize), sort: 'period', order: 'DESC' })
      setMyPage(await apiJson<Page<Payslip>>(`/payslips/me?${params}`))
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load payslips')
    } finally {
      setLoading(false)
    }
  }, [myPageNum, mySize])

  const fetchAll = useCallback(async () => {
    if (!allowManage) return
    try {
      const params = new URLSearchParams({ page: String(allPageNum), size: String(allSize), sort: allSort, order: allOrder })
      if (q) params.set('q', q)
      if (periodFilter) params.set('period', periodFilter)
      if (empFilter) params.set('employeeId', empFilter)
      if (deptFilter) params.set('orgUnitId', deptFilter)
      setAllPage(await apiJson<Page<Payslip>>(`/payslips?${params}`))
    } catch {
      // admin list is best-effort
    }
  }, [allowManage, allPageNum, allSize, allSort, allOrder, q, periodFilter, empFilter, deptFilter])

  useEffect(() => {
    fetchMine()
  }, [fetchMine])
  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  useEffect(() => {
    if (!allowManage) return
    apiJson<Page<Employee>>('/employees?size=500&sort=firstName&order=ASC')
      .then((p) => setEmployees(p.data))
      .catch(() => {})
    apiJson<{ id: string; name: string }[]>('/org-units')
      .then(setOrgUnits)
      .catch(() => {})
  }, [allowManage])

  // ---- payroll runs ----
  const fetchRuns = useCallback(async () => {
    if (!allowManage) return
    try {
      setRuns(await apiJson<PayrollRun[]>('/payroll-runs'))
    } catch {
      // runs tab is best-effort
    }
  }, [allowManage])

  useEffect(() => {
    if (tab === 'runs') fetchRuns()
  }, [tab, fetchRuns])

  const openRunDetail = async (run: PayrollRun) => {
    setRunDetail(run)
    setRunSummary([])
    try {
      setRunSummary(await apiJson<DeptSummary[]>(`/payroll-runs/${run.id}/summary`))
    } catch {
      // summary is best-effort
    }
  }

  const finalizeRun = async () => {
    if (!runDetail) return
    if (!window.confirm(`Finalize the ${periodLabel(runDetail.period)} run? Draft payslips will be issued (employees notified) and the period locked.`)) return
    setRunBusy(true)
    setActionError('')
    try {
      await apiJson(`/payroll-runs/${runDetail.id}/finalize`, { method: 'POST' })
      setRunDetail(null)
      await Promise.all([fetchRuns(), fetchAll(), fetchMine()])
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Finalize failed')
    } finally {
      setRunBusy(false)
    }
  }

  const exportRun = async (run: PayrollRun) => {
    try {
      const res = await apiFetch(`/payroll-runs/${run.id}/export`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payroll-${run.period}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Export failed')
    }
  }

  const myRows = useMemo(() => myPage?.data ?? [], [myPage])
  const allRows = useMemo(() => allPage?.data ?? [], [allPage])
  const latest = myRows.length > 0 && myPageNum === 1 ? myRows[0] : null

  const handleSort = (field: string) => {
    if (allSort === field) {
      setAllOrder(allOrder === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setAllSort(field)
      setAllOrder(field === 'period' ? 'DESC' : 'ASC')
    }
    setAllPageNum(1)
  }

  const remove = async (slip: Payslip) => {
    if (!window.confirm(`Delete the ${periodLabel(slip.period)} payslip for ${slip.employeeName}?`)) return
    setDeletingId(slip.id)
    setActionError('')
    try {
      await apiJson(`/payslips/${slip.id}`, { method: 'DELETE' })
      await fetchAll()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Latest payslip highlight */}
      {latest && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 bg-gradient-to-br from-brand-600 via-brand-500 to-brand-700 px-6 py-6 text-white sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/70">Latest payslip · {periodLabel(latest.period)}</p>
              <p className="mt-1 text-3xl font-extrabold tracking-tight">{money(latest.netPay, currency)}</p>
              <p className="mt-1 text-sm text-white/80">
                base {money(latest.baseSalary, currency)} + allowances {money(latest.allowances, currency)} − deductions {money(latest.deductions, currency)}
              </p>
            </div>
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/30">
              <Wallet size={24} />
            </span>
          </div>
        </div>
      )}

      {(loadError || actionError) && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {loadError || actionError}
        </div>
      )}

      {/* Tabs + actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {allowManage ? (
          <div className="flex gap-1 rounded-xl bg-slate-100 p-1 sm:w-fit">
            <button
              onClick={() => setTab('mine')}
              className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${tab === 'mine' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              My payslips
            </button>
            <button
              onClick={() => setTab('all')}
              className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${tab === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              All payslips
            </button>
            <button
              onClick={() => setTab('runs')}
              className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${tab === 'runs' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Runs
            </button>
          </div>
        ) : <div />}
        {allowManage && tab === 'all' && (
          <button
            onClick={() => navigate('/payroll/payslips/new')}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:brightness-110"
          >
            <Plus size={16} />
            New payslip
          </button>
        )}
        {allowManage && tab === 'runs' && (
          <button
            onClick={() => navigate('/payroll/runs/new')}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:brightness-110"
          >
            <Plus size={16} />
            New run
          </button>
        )}
      </div>

      {/* My payslips */}
      {tab === 'mine' && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Period</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Base ({currency})</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Allowances</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Deductions</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Net pay</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {myRows.map((p) => (
                  <tr key={p.id} className="transition-colors hover:bg-slate-50/70">
                    <td className="px-5 py-3.5 text-sm font-semibold">{periodLabel(p.period)}</td>
                    <td className="px-5 py-3.5 text-right text-sm text-slate-500">{p.baseSalary.toLocaleString('en-US')}</td>
                    <td className="px-5 py-3.5 text-right text-sm text-emerald-600">+{p.allowances.toLocaleString('en-US')}</td>
                    <td className="px-5 py-3.5 text-right text-sm text-red-500">−{p.deductions.toLocaleString('en-US')}</td>
                    <td className="px-5 py-3.5 text-right text-sm font-bold">{p.netPay.toLocaleString('en-US')}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${statusMeta[p.status]?.cls ?? ''}`}>
                        {statusMeta[p.status]?.label ?? p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {myRows.length === 0 && (
            <div className="flex flex-col items-center py-14 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Banknote size={22} />
              </div>
              <p className="mt-3 text-sm font-semibold">No payslips yet</p>
              <p className="mt-1 text-xs text-slate-400">Your payslips will appear here once payroll issues them.</p>
            </div>
          )}
          <Pager
            page={myPageNum}
            totalPages={myPage?.totalPages ?? 1}
            total={myPage?.total ?? 0}
            size={mySize}
            onPage={setMyPageNum}
            onSize={(s) => { setMySize(s); setMyPageNum(1) }}
          />
        </div>
      )}

      {/* All payslips (admin/hr) */}
      {tab === 'all' && allowManage && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search employee…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 sm:w-64"
              />
            </div>
            <select
              value={empFilter}
              onChange={(e) => { setEmpFilter(e.target.value); setAllPageNum(1) }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            >
              <option value="">All employees</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.firstName} {e.lastName} ({e.employeeCode})
                </option>
              ))}
            </select>
            <select
              value={deptFilter}
              onChange={(e) => { setDeptFilter(e.target.value); setAllPageNum(1) }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            >
              <option value="">All departments</option>
              {orgUnits.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <input
              type="month"
              value={periodFilter}
              onChange={(e) => { setPeriodFilter(e.target.value); setAllPageNum(1) }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            />
            {(periodFilter || empFilter || deptFilter) && (
              <button onClick={() => { setPeriodFilter(''); setEmpFilter(''); setDeptFilter(''); setAllPageNum(1) }} className="text-xs font-semibold text-brand-600 hover:text-brand-700">
                Clear filters
              </button>
            )}
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <SortableTh label="Employee" field="employeeName" sort={allSort} order={allOrder} onSort={handleSort} />
                    <SortableTh label="Period" field="period" sort={allSort} order={allOrder} onSort={handleSort} />
                    <SortableTh label="Base" field="baseSalary" sort={allSort} order={allOrder} onSort={handleSort} align="right" />
                    <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Net pay</th>
                    <SortableTh label="Status" field="status" sort={allSort} order={allOrder} onSort={handleSort} />
                    <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {allRows.map((p) => (
                    <tr key={p.id} className="group transition-colors hover:bg-slate-50/70">
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-semibold">{p.employeeName}</p>
                        <p className="text-xs text-slate-400">{p.employeeCode}</p>
                      </td>
                      <td className="px-5 py-3.5 text-sm font-semibold">{p.period}</td>
                      <td className="px-5 py-3.5 text-right text-sm text-slate-500">{p.baseSalary.toLocaleString('en-US')}</td>
                      <td className="px-5 py-3.5 text-right text-sm font-bold">{p.netPay.toLocaleString('en-US')}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${statusMeta[p.status]?.cls ?? ''}`}>
                          {statusMeta[p.status]?.label ?? p.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="inline-flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            onClick={() => navigate(`/payroll/payslips/${p.id}/edit`, { state: { slip: p } })}
                            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                            title="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          {allowDelete && (
                            <button
                              onClick={() => remove(p)}
                              disabled={deletingId === p.id}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                              title="Delete (admin only)"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {allRows.length === 0 && (
              <div className="flex flex-col items-center py-14 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                  <Banknote size={22} />
                </div>
                <p className="mt-3 text-sm font-semibold">No payslips found</p>
                <p className="mt-1 text-xs text-slate-400">Create the first payslip to get started.</p>
              </div>
            )}
            <Pager
              page={allPageNum}
              totalPages={allPage?.totalPages ?? 1}
              total={allPage?.total ?? 0}
              size={allSize}
              onPage={setAllPageNum}
              onSize={(s) => { setAllSize(s); setAllPageNum(1) }}
            />
          </div>
        </div>
      )}

      {/* Payroll runs */}
      {tab === 'runs' && allowManage && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Period</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Slips created</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Employees</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Total net ({currency})</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Generated by</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {runs.map((r) => (
                  <tr key={r.id} className="transition-colors hover:bg-slate-50/70">
                    <td className="px-5 py-3.5 text-sm font-semibold">{periodLabel(r.period)}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                        r.status === 'finalized'
                          ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                          : 'bg-slate-100 text-slate-500 ring-slate-200'
                      }`}>
                        {r.status === 'finalized' ? 'Finalized' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right text-sm text-slate-500">{r.payslipsCreated}</td>
                    <td className="px-5 py-3.5 text-right text-sm text-slate-500">{r.totalEmployees}</td>
                    <td className="px-5 py-3.5 text-right text-sm font-bold">{money(r.totalNet, currency)}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{r.generatedBy ?? '—'}</td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openRunDetail(r)}
                          className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-brand-600 transition-colors hover:bg-brand-50"
                        >
                          Summary
                        </button>
                        <button
                          onClick={() => exportRun(r)}
                          className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-100"
                        >
                          CSV
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {runs.length === 0 && (
            <div className="flex flex-col items-center py-14 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Banknote size={22} />
              </div>
              <p className="mt-3 text-sm font-semibold">No payroll runs yet</p>
              <p className="mt-1 text-xs text-slate-400">Generate a run to batch-create draft payslips for a period.</p>
            </div>
          )}
        </div>
      )}

      {/* Run detail + department summary modal */}
      {runDetail && (
        <Modal
          title={`Run ${periodLabel(runDetail.period)} — department summary`}
          subtitle={runDetail.status === 'finalized'
            ? `Finalized by ${runDetail.finalizedBy ?? '—'} · period locked`
            : 'Draft — finalize to issue payslips and lock the period'}
          onClose={() => setRunDetail(null)}
          wide
        >
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Department</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Employees</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Base</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Net ({currency})</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {runSummary.map((d) => (
                    <tr key={d.orgUnitId ?? 'unassigned'}>
                      <td className="px-4 py-2.5 text-sm font-medium">{d.orgUnitName}</td>
                      <td className="px-4 py-2.5 text-right text-sm text-slate-500">{d.employees}</td>
                      <td className="px-4 py-2.5 text-right text-sm text-slate-500">{d.totalBase.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold">{d.totalNet.toLocaleString('en-US')}</td>
                    </tr>
                  ))}
                  {runSummary.length > 0 && (
                    <tr className="bg-slate-50/70 font-semibold">
                      <td className="px-4 py-2.5 text-sm">Total</td>
                      <td className="px-4 py-2.5 text-right text-sm">{runSummary.reduce((s, d) => s + d.employees, 0)}</td>
                      <td className="px-4 py-2.5 text-right text-sm">{runSummary.reduce((s, d) => s + d.totalBase, 0).toLocaleString('en-US')}</td>
                      <td className="px-4 py-2.5 text-right text-sm">{runSummary.reduce((s, d) => s + d.totalNet, 0).toLocaleString('en-US')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {runSummary.length === 0 && (
                <p className="px-4 py-6 text-center text-xs text-slate-400">Loading summary…</p>
              )}
            </div>
            <div className="flex items-center justify-between">
              <button
                onClick={() => exportRun(runDetail)}
                className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-500 ring-1 ring-slate-200 transition-colors hover:bg-slate-50"
              >
                Export CSV
              </button>
              {runDetail.status === 'draft' && allowDelete && (
                <button
                  onClick={finalizeRun}
                  disabled={runBusy}
                  className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700 disabled:opacity-50"
                >
                  {runBusy ? 'Finalizing…' : 'Finalize & issue'}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
